/**
 * viewDeclToDraft — the INVERSE of `clauseModel.rowText` + `useViewDrafts`'
 * draft→text compile: turn a resolved `ViewDeclV2` back into the builder's
 * editable `ViewDraft` (name + structured `ClauseRow`s), so opening the wizard
 * while a view is active prepopulates it.
 *
 * Structured for every clause shape the rows hold — show/hide/focus (whose
 * selector maps onto the full `SelTerm` tree, so nesting, arrows and extraction
 * survive), group by, at, on, color/style, flow, table, matrix, grid, index.
 * What the typed rows still can't losslessly hold (weave `on a, b`, calc
 * columns, aspect webs, lens…) degrades to a `raw` row carrying the clause's
 * EXACT source text (sliced from `compiledSources`), so nothing is dropped or
 * corrupted.
 */
import type {
  ViewDeclV2,
  ViewClause,
  Selector,
  SelHop,
  ExprGetter,
  Expr,
  StyleMember,
  FlowBodyClause,
  Value,
  SourceSpan,
  ScrubPoint,
} from "@archlang/engine";
import {
  BUILTIN_KIND_ATOMS,
  seedShowAll,
  type ClauseRow,
  type SelectorAtom,
  type StyleColorValue,
  type StyleValue,
  type StyleMemberRow,
  type StyleTarget,
  type Refinement,
  type FlowBodyRow,
  type FlowMode,
  type MatrixOrder,
  type TableColumnRow,
  type ScrubPointRow,
  type SelTerm,
} from "./clauseModel.js";
import type { ViewDraft } from "./useViewDrafts.js";

/** Slice a span's exact source text from the compiled sources; null if absent. */
export type SliceSource = (span?: SourceSpan) => string | null;

export function sliceSourceFrom(sources: ReadonlyMap<string, string> | null | undefined): SliceSource {
  return (span) => {
    if (!span?.file || !sources) return null;
    // Compiled VFS keys are absolute (leading /); spans may carry either form.
    const text = sources.get(span.file) ?? sources.get("/" + span.file) ?? sources.get(span.file.replace(/^\//, ""));
    if (text === undefined) return null;
    return text.slice(span.start.offset, span.end.offset);
  };
}

/** An aspect value in its WRITTEN form (`"Ops"` / `Euro.DC1`), or null if it's
 *  a shape the simple atom can't hold (number/list/boolean/$knob). */
function aspectValueText(v: Value): string | null {
  switch (v.kind) {
    case "string":
      return `"${v.text}"`;
    case "identifier":
      return v.text;
    case "ref":
      return v.path.join(".");
    default:
      return null;
  }
}

/** A single node selector → one builder atom, or null if not a simple atom. */
function atomOf(sel: Selector): SelectorAtom | null {
  switch (sel.kind) {
    case "universal":
      return { kind: "all" };
    case "ref": {
      const path = sel.path.join(".");
      if (sel.path.length === 1 && (BUILTIN_KIND_ATOMS as readonly string[]).includes(sel.path[0]!))
        return { kind: "type", name: sel.path[0]! };
      return { kind: "element", path };
    }
    case "aspect": {
      const key = sel.path.join(".");
      if (!sel.value) return { kind: "aspect", key };
      if ("knob" in sel.value) return null;
      const value = aspectValueText(sel.value);
      return value === null ? null : { kind: "aspect", key, value };
    }
    default:
      return null;
  }
}

/** An `or` selector's terms → an atom list, or null if any term is not a simple
 *  atom (a compound term ⇒ the whole union is unmodellable). */
function atomsUnion(terms: readonly Selector[]): SelectorAtom[] | null {
  const out: SelectorAtom[] = [];
  for (const t of terms) {
    const a = atomOf(t);
    if (!a) return null;
    out.push(a);
  }
  return out;
}

/**
 * A hop's `within` bound → the builder's text field.
 *
 * `WithinBound` is `{ value: number | { knob } }`, NOT a number — stringifying
 * the wrapper wrote `within [object Object]` into the user's `.arch`, which no
 * longer parses. Opening the builder on `A >> B within 3` and pressing save was
 * enough to do it.
 */
function withinText(within: SelHop["within"]): string {
  if (within === undefined) return "";
  const v = within.value;
  return typeof v === "number" ? String(v) : `$${v.knob}`;
}

/**
 * A parsed selector → a builder TERM. Near-total: every combinator, predicate,
 * arrow and extraction has a term shape, so a hand-written selector opens in
 * the pickers instead of collapsing the whole clause to raw text. The residue
 * — `this`, `$knob`, `@deco` atoms, an `in` whose argument is not a bare ref, a
 * `where` whose expression cannot be sliced — becomes ONE `raw` leaf carrying
 * its own source, leaving its siblings structured.
 */
function termOf(sel: Selector, src: SliceSource): SelTerm {
  const raw = (): SelTerm => ({ kind: "raw", text: (src(sel.source) ?? "").trim() });
  const atom = atomOf(sel);
  if (atom) return { kind: "atom", atom };
  switch (sel.kind) {
    case "and":
    case "or":
      return { kind: sel.kind, terms: sel.terms.map((t) => termOf(t, src)) };
    case "not":
      return { kind: "not", term: termOf(sel.term, src) };
    case "outside":
      return { kind: "outside" };
    case "violating":
      return { kind: "violating", policy: sel.policy.join(".") };
    case "on":
      return { kind: "on", plane: sel.plane };
    case "in": {
      // The scope is whatever the containment side names — a bare ref, or any
      // selector (`in (A or B)`, `in domain`, SELECTORS §3). A ref is rebuilt
      // from its path; anything else keeps the exact text it was written with,
      // which the picker shows verbatim and the emit gives back untouched.
      const scope = sel.arg.kind === "ref" ? sel.arg.path.join(".") : src(sel.arg.source)?.trim();
      if (!scope) return raw();
      const depth = withinText(sel.depth);
      return { kind: "in", scope, ...(depth ? { depth } : {}) };
    }
    case "depth":
      return { kind: "depth", depth: withinText(sel.bound) };
    case "where": {
      // Slice the INNER expr span (not the whole `where (…)`) — the emit re-wraps.
      const expr = src(sel.expr.source);
      return expr && expr.trim() ? { kind: "where", expr: expr.trim() } : raw();
    }
    case "extraction":
      return { kind: "extract", head: sel.head, inner: termOf(sel.inner, src) };
    case "arrow": {
      // A chain (`a > b > c`) is one clause with several hops — the builder's
      // edge term is a single hop, so a multi-hop chain keeps its exact text.
      if (sel.hops.length !== 1) return raw();
      const hop = sel.hops[0]!;
      return {
        kind: "edge",
        from: termOf(sel.head, src),
        op: hop.op,
        within: withinText(hop.within),
        to: termOf(hop.rhs, src),
      };
    }
    default:
      return raw();
  }
}

/** A non-base conjunction term → one refinement, or null (→ the clause is raw).
 *  `not <atom>` / `in <ref>` / `where (<expr>)` / a positive atom (`and <atom>`).
 *  A `not (union)`, an `in` with a this/star/aspect/union arg, or an unsliceable
 *  `where` → raw. Still the STYLE node target's shape (selection clauses moved
 *  to the full `SelTerm` tree). */
function refinementOf(sel: Selector, src: SliceSource): Refinement | null {
  if (sel.kind === "not") {
    const a = atomOf(sel.term);
    return a ? { kind: "notAtom", atom: a } : null;
  }
  if (sel.kind === "in") {
    // Any container selector is a scope — a ref by path, the rest by their
    // exact text (same rule as the selection-clause term above).
    const scope = sel.arg.kind === "ref" ? sel.arg.path.join(".") : src(sel.arg.source)?.trim();
    if (!scope) return null;
    const depth = withinText(sel.depth);
    return { kind: "in", scope, ...(depth ? { depth } : {}) };
  }
  if (sel.kind === "where") {
    // Slice the INNER expr span (not the whole `where (…)`) — the emit re-wraps.
    const expr = src(sel.expr.source);
    return expr && expr.trim() ? { kind: "where", expr: expr.trim() } : null;
  }
  const a = atomOf(sel);
  return a ? { kind: "andAtom", atom: a } : null;
}

/** A `SelAnd` conjunction → a base union + refinements. The FIRST term, if an atom
 *  or an atom-only `or`, is the base; a leading predicate ⇒ empty base. Later terms
 *  map to refinements; a non-leading `or` (a second union) → null (→ raw). */
function conjunctionOf(terms: readonly Selector[], src: SliceSource): { atoms: SelectorAtom[]; refinements: Refinement[] } | null {
  let atoms: SelectorAtom[] = [];
  const refinements: Refinement[] = [];
  for (let i = 0; i < terms.length; i++) {
    const t = terms[i]!;
    if (i === 0 && t.kind === "or") {
      const u = atomsUnion(t.terms);
      if (!u) return null; // a compound union base → raw
      atoms = u;
      continue;
    }
    if (i === 0) {
      const a = atomOf(t);
      if (a) {
        atoms = [a];
        continue;
      }
      // leading predicate (not/in/where) → empty base; fall through as a refinement
    }
    if (t.kind === "or") return null; // a non-leading union → raw (one base only)
    const ref = refinementOf(t, src);
    if (!ref) return null;
    refinements.push(ref);
  }
  return { atoms, refinements };
}

/** A node selector → a base atom union + a refinement list, or null (→ raw).
 *  The STYLE node target's inverse of `refineText`. */
function nodeSelectorOf(sel: Selector, src: SliceSource): { atoms: SelectorAtom[]; refinements: Refinement[] } | null {
  const bare = atomOf(sel);
  if (bare) return { atoms: [bare], refinements: [] };
  if (sel.kind === "or") {
    const u = atomsUnion(sel.terms);
    return u ? { atoms: u, refinements: [] } : null;
  }
  if (sel.kind === "and") return conjunctionOf(sel.terms, src);
  // top-level single predicate (not/in/where) → empty base + one refinement
  const ref = refinementOf(sel, src);
  return ref ? { atoms: [], refinements: [ref] } : null;
}

const aspectKeyOf = (g: ExprGetter): string | null => (g.sigil === "@@" ? g.path.join(".") : null);

/** A getter in its written form (`@name`, `@@team`) — sliced when possible. */
function getterText(g: ExprGetter, src: SliceSource): string {
  const sliced = src(g.source);
  if (sliced) return sliced.trim();
  return (g.sigil === "@@" ? "@@" : "@") + g.path.join(".") + (g.capabilityArgs ? `(${g.capabilityArgs})` : "");
}

/** A `color: <expr>` value → the builder colour, or null if unmodellable
 *  (`css(@field)`, a computed expr, a bare uncast getter). */
function colorValueOf(e: Expr): StyleColorValue | null {
  if (e.kind === "call" && (e.fn === "colorize" || e.fn === "heat") && e.args.length >= 1) {
    const g = e.args[0]!;
    if (g.kind === "getter" && g.sigil === "@@") return { kind: e.fn, key: g.path.join(".") };
    return null;
  }
  if (e.kind === "literal" && e.value.kind === "string") return { kind: "hex", value: e.value.text };
  return null;
}

/** A widget-leaf `<prop>: <expr>` value → a builder value, PRESERVING the token
 *  kind (ident/string/getter); null if computed/number/other (→ raw fallback). */
function styleValueOf(e: Expr, src: SliceSource): StyleValue | null {
  if (e.kind === "getter") return { kind: "getter", getter: getterText(e, src) };
  if (e.kind === "literal" && e.value.kind === "identifier") return { kind: "ident", text: e.value.text };
  if (e.kind === "literal" && e.value.kind === "string") return { kind: "string", text: e.value.text };
  return null;
}

/** A numeric literal's value, or null (a getter/string/computed RHS). */
function numberLiteralOf(e: Expr): number | null {
  return e.kind === "literal" && e.value.kind === "number" ? e.value.value : null;
}

/** A `style` subject → its builder target, or null (→ raw). `node` = a simple atom
 *  (`*`/type/element/aspect); `edge` = a single-hop `A > B` (op `>` only — `>>`/`<>`,
 *  a `within` bound, and chains stay raw until proven §3.1); `policy` = `violating <P>`. */
function styleTargetOf(sel: Selector, src: SliceSource): StyleTarget | null {
  if (sel.kind === "arrow") {
    if (sel.hops.length !== 1) return null; // chain → raw
    const hop = sel.hops[0]!;
    if (hop.within !== undefined || hop.op !== ">") return null; // within / >>/<> → raw
    const from = atomOf(sel.head);
    const to = atomOf(hop.rhs);
    return from && to ? { kind: "edge", from, op: ">", to } : null;
  }
  if (sel.kind === "violating") return { kind: "policy", policy: sel.policy.join(".") };
  // node — a SINGLE base atom + optional refinements (a union base → raw).
  const ns = nodeSelectorOf(sel, src);
  return ns && ns.atoms.length === 1 ? { kind: "node", atom: ns.atoms[0]!, refinements: ns.refinements } : null;
}

/** A style body → structured member rows, or null (→ the whole clause degrades to
 *  raw) when ANY member is unmodellable. The `widget { … }` group form explodes to
 *  flat `widget.<prop>` members (both forms compile identically; the builder's
 *  canonical emit is flat). `width` is recognised only under an edge/policy target
 *  (`allowWidth` — a node paints no width, so a node `width:` body → raw). Only
 *  genuinely non-representable bodies fall to raw — a css/computed colour, an unknown
 *  field path, a non-`widget` group — NOT merely a body with more than one member. */
function styleMembersOf(members: readonly StyleMember[], src: SliceSource, allowWidth: boolean): StyleMemberRow[] | null {
  const out: StyleMemberRow[] = [];
  const walk = (ms: readonly StyleMember[], prefix: readonly string[]): boolean => {
    for (const m of ms) {
      switch (m.kind) {
        case "rewrite": {
          const path = [...prefix, ...m.path];
          if (path.length === 1 && path[0] === "color") {
            const cv = colorValueOf(m.value);
            if (!cv) return false;
            out.push({ kind: "color", value: cv });
          } else if (allowWidth && path.length === 1 && path[0] === "width") {
            const px = numberLiteralOf(m.value);
            if (px === null) return false;
            out.push({ kind: "width", px });
          } else if (path.length === 2 && path[0] === "widget") {
            const sv = styleValueOf(m.value, src);
            if (!sv) return false;
            out.push({ kind: "widget", prop: path[1]!, value: sv });
          } else {
            return false; // unknown leaf / deep path → raw
          }
          break;
        }
        case "group":
          // Only `widget { … }` explodes to flat widget members.
          if (m.path.length !== 1 || m.path[0] !== "widget") return false;
          if (!walk(m.members, ["widget"])) return false;
          break;
        case "rename":
          out.push({ kind: "rename", label: m.label });
          break;
        case "pin":
          out.push({ kind: "pin", x: m.x, y: m.y });
          break;
        case "use":
          out.push({ kind: "use", bundle: m.bundle });
          break;
        default:
          return false;
      }
    }
    return true;
  };
  return walk(members, []) ? out : null;
}

function flowBodyRowOf(b: FlowBodyClause): FlowBodyRow | null {
  switch (b.kind) {
    case "lane-expand": {
      const a = atomOf(b.selector);
      return a ? { kind: "lane-expand", selector: a } : null;
    }
    case "lane-merge": {
      const a = atomOf(b.selector);
      return a ? { kind: "lane-merge", title: b.title, selector: a } : null;
    }
    case "pool-merge": {
      const a = atomOf(b.selector);
      return a ? { kind: "pool-merge", title: b.title, selector: a } : null;
    }
    case "lane-by": {
      const k = aspectKeyOf(b.getter);
      return k ? { kind: "lane-by", key: k } : null;
    }
    case "pool-by": {
      const k = aspectKeyOf(b.getter);
      return k ? { kind: "pool-by", key: k } : null;
    }
    case "pool-ref":
      return { kind: "pool-ref", ref: b.ref.kind === "ref" ? b.ref.path.join(".") : "$" + b.ref.name };
    default:
      // replicate / engine — no structured row; caller degrades the flow to raw.
      return null;
  }
}

/** A parsed scrub point → its row form. A marker's `text` is the WRITTEN token
 *  (the parser keeps a quoted one quoted), so it re-emits byte-identically. */
function scrubPointRowOf(p: ScrubPoint): ScrubPointRow {
  return p.kind === "now" ? { kind: "now" } : { kind: "marker", text: p.text };
}

/** One clause → its structured row, or null to signal a raw fallback. */
function rowOf(c: ViewClause, src: SliceSource): ClauseRow | null {
  switch (c.kind) {
    case "show":
    case "hide":
    case "focus":
      return { kind: c.kind, sel: termOf(c.selector, src) };
    case "groupBy": {
      // `group by in <sel>` — the containment form. Its selector is kept as
      // WRITTEN text (the row is a scope string, not a term tree).
      if (c.scope !== undefined) {
        const text = src(c.scope.source)?.trim();
        return text ? { kind: "groupBy", key: "", scope: text } : null;
      }
      const key = c.getter && aspectKeyOf(c.getter);
      return key ? { kind: "groupBy", key } : null;
    }
    case "at":
      return {
        kind: "at",
        at: scrubPointRowOf(c.at),
        ...(c.compare ? { compare: scrubPointRowOf(c.compare) } : {}),
      };
    case "on":
      // A single plane is the switcher's board; `on a, b` weave has no row.
      return c.planes.length === 1 ? { kind: "on", plane: c.planes[0]! } : null;
    case "style": {
      const target = styleTargetOf(c.subject, src);
      if (!target) return null; // unmodellable subject → raw
      const members = styleMembersOf(c.members, src, target.kind !== "node");
      if (!members || members.length === 0) return null; // unmodellable body → raw
      // `style * { color: colorize(@@k) }` as the SOLE member (and no refinements)
      // collapses to the top "colour by aspect" shortcut row.
      const only = members.length === 1 ? members[0]! : null;
      if (
        target.kind === "node" &&
        target.atom.kind === "all" &&
        (target.refinements?.length ?? 0) === 0 &&
        only?.kind === "color" &&
        only.value.kind === "colorize"
      ) {
        return { kind: "color", key: only.value.key };
      }
      return { kind: "style", selector: target, members };
    }
    case "flow": {
      const body: FlowBodyRow[] = [];
      for (const b of c.body ?? []) {
        const r = flowBodyRowOf(b);
        if (!r) return null;
        body.push(r);
      }
      const ref = c.ref.kind === "ref" ? c.ref.path.join(".") : "$" + c.ref.name;
      return { kind: "flow", ref, mode: (c.mode ?? "plain") as FlowMode, body };
    }
    case "table": {
      const columns: TableColumnRow[] = [];
      for (const col of c.columns) {
        if (col.kind !== "getter") return null; // calc column → raw
        columns.push({ getter: getterText(col.getter, src), title: col.title ?? "" });
      }
      if (columns.length === 0) return null;
      return { kind: "table", columns, sortBy: c.sortBy ? getterText(c.sortBy, src) : "" };
    }
    case "matrix": {
      if (c.axis && aspectKeyOf(c.axis) === null) return null; // non-aspect axis → raw
      if ((c as { rows?: unknown }).rows || (c as { cols?: unknown }).cols) return null; // rectangular → raw
      const axis = c.axis ? aspectKeyOf(c.axis)! : "";
      const strat = c.order?.strategy;
      let order: MatrixOrder;
      let orderKey = "";
      if (strat?.kind === "layer") order = "layer";
      else if (strat?.kind === "getter") {
        const k = aspectKeyOf(strat.getter);
        if (!k) return null;
        order = "aspect";
        orderKey = k;
      } else if (strat?.kind === "cluster" || c.cluster) order = "cluster";
      else if (!axis) return null; // bare `matrix {}` — keep exact via raw
      else order = "none";
      return { kind: "matrix", axis, order, orderKey };
    }
    case "grid": {
      const rows = c.rows ? aspectKeyOf(c.rows) : "";
      const cols = c.cols ? aspectKeyOf(c.cols) : "";
      const color = c.color ? aspectKeyOf(c.color) : "";
      if (rows === null || cols === null || color === null) return null; // non-aspect getter → raw
      if (!rows && !cols) return null; // needs an axis
      return { kind: "grid", rows: rows ?? "", cols: cols ?? "", color: color ?? "" };
    }
    case "index": {
      return {
        kind: "index",
        sortBy: c.sortBy ? getterText(c.sortBy, src) : "",
        columns: c.columns !== undefined ? String(c.columns) : "",
      };
    }
    default:
      // lens (and any future clause) → raw.
      return null;
  }
}

/** Convert a resolved view into an editable draft. Unrepresentable clauses keep
 *  their exact source as `raw` rows; a clause with no sliceable source is
 *  dropped rather than emitting an empty row. */
export function viewDeclToDraft(view: ViewDeclV2, src: SliceSource): ViewDraft {
  const rows: ClauseRow[] = [];
  // Knob HEADER decls first — the builder has no structured knob row, so each
  // decl rides as a raw row carrying its exact source, re-emitted verbatim
  // ahead of the clauses. That ORDER is required, not cosmetic: a `knob` after
  // the first clause is a parse error (`parse.viewKnobBeforeClause`), and a
  // draft that preserved such a position would hand `setKnobs` and `setClauses`
  // overlapping regions, which `applyTextEdits` throws on. A block-form enum
  // entry's span starts at its NAME (no `knob` keyword) — re-wrap it.
  for (const k of view.header.knobs) {
    const text = (src(k.source) ?? "").trim();
    if (!text) continue;
    // Whole-word `knob` prefix — a block-form entry whose NAME merely starts
    // with "knob" (`knobiness one of: …`) still needs the wrapper.
    const startsAtKeyword = /^knob(?!\p{ID_Continue})/u.test(text);
    rows.push({ kind: "raw", text: startsAtKeyword ? text : `knob { ${text} }` });
  }
  for (const c of view.clauses) {
    const structured = rowOf(c, src);
    if (structured) {
      rows.push(structured);
      continue;
    }
    const raw = (src(c.source) ?? "").trim();
    if (raw) rows.push({ kind: "raw", text: raw });
  }
  return { name: view.name, rows: rows.length ? rows : [seedShowAll()] };
}
